<div class="reporting-shell">
  <section class="reporting-header">
    <div>
      <p class="eyebrow">{{ activeWorkspace === 'sql' ? 'Data Modeling & Analytics' : (activeWorkspace === 'templates' ? 'Document Automation' : 'Self-service reporting') }}</p>
      <h1>{{ activeWorkspace === 'sql' ? 'SQL Data Models' : (activeWorkspace === 'templates' ? 'Template Editor' : 'Report Builder') }}</h1>
      <p class="subtitle">{{ activeWorkspace === 'sql' ? 'Author secure PostgreSQL SELECT models, bind parameters, preview live results, and publish virtual company sources.' : (activeWorkspace === 'templates' ? 'Design and customize business document templates with Thymeleaf bindings.' : 'Create curated operational reports, preview them safely, and export to PDF, Excel, CSV, Word, or PowerPoint.') }}</p>
    </div>
    <div class="header-actions">
      <button pButton type="button" icon="pi pi-chart-bar" label="Report Builder" [outlined]="activeWorkspace !== 'builder'" (click)="switchWorkspace('builder')"></button>
      <button *ngIf="canViewSql" pButton type="button" icon="pi pi-database" label="SQL Data Models" [outlined]="activeWorkspace !== 'sql'" (click)="switchWorkspace('sql')"></button>
      <button *ngIf="canViewTemplates" pButton type="button" icon="pi pi-code" label="Template Editor" [outlined]="activeWorkspace !== 'templates'" (click)="switchWorkspace('templates')"></button>
      <button *ngIf="activeWorkspace === 'builder'" pButton type="button" icon="pi pi-plus" label="New" [outlined]="true" (click)="newReport()"></button>
      <button *ngIf="activeWorkspace === 'builder'" pButton type="button" icon="pi pi-eye" label="Preview" [loading]="loading" (click)="previewReport()"></button>
      <button *ngIf="activeWorkspace === 'builder'" pButton type="button" icon="pi pi-save" label="Save" [loading]="saving" (click)="saveReport()"></button>
      <button *ngIf="activeWorkspace === 'sql'" pButton type="button" icon="pi pi-plus" label="New" [outlined]="true" [disabled]="!canEditSql" (click)="newSqlQuery()"></button>
      <button *ngIf="activeWorkspace === 'sql'" pButton type="button" icon="pi pi-check-circle" label="Validate" [outlined]="true" [loading]="sqlLoading" (click)="validateSqlQuery()"></button>
      <button *ngIf="activeWorkspace === 'sql'" pButton type="button" icon="pi pi-play" label="Run Preview" [loading]="sqlLoading" [disabled]="!canExecuteSql" (click)="previewSqlQuery()"></button>
      <button *ngIf="activeWorkspace === 'sql'" pButton type="button" icon="pi pi-save" label="Save Draft" [loading]="sqlSaving" [disabled]="!canEditSelectedSql()" (click)="saveSqlQuery()"></button>
      <button *ngIf="activeWorkspace === 'sql' && selectedSqlQuery?.queryId && selectedSqlQuery?.editable && canPublishSql && !selectedSqlQuery?.published" pButton type="button" icon="pi pi-share-alt" label="Publish" [outlined]="true" [loading]="sqlSaving" (click)="publishSqlQuery()"></button>
      <button *ngIf="activeWorkspace === 'sql' && selectedSqlQuery?.queryId && selectedSqlQuery?.editable && canPublishSql && selectedSqlQuery?.published" pButton type="button" icon="pi pi-eye-slash" label="Unpublish" [outlined]="true" [loading]="sqlSaving" (click)="unpublishSqlQuery()"></button>
    </div>
  </section>

  <p-progressBar *ngIf="loading || sqlLoading || sqlExportingFormat" mode="indeterminate" [style]="{ height: '4px' }"></p-progressBar>
  <div *ngIf="errorMessage" class="error-panel">{{ errorMessage }}</div>

  <section class="workspace-grid" [class.right-collapsed]="rightPaneCollapsed" [class.left-collapsed]="leftPaneCollapsed" *ngIf="activeWorkspace === 'builder'">
    <aside class="catalog-panel bi-tree-panel" [class.collapsed]="leftPaneCollapsed">
      <!-- COLLAPSED RAIL -->
      <div class="left-pane-rail" *ngIf="leftPaneCollapsed">
        <button
          type="button"
          class="rail-toggle-btn"
          (click)="toggleLeftPane()"
          title="Expand Data Model Explorer">
          <i class="pi pi-angle-double-right"></i>
        </button>
        <div class="rail-divider"></div>
        <button
          type="button"
          class="rail-icon-btn"
          (click)="toggleLeftPane()"
          title="Data Sets ({{ datasets.length }})">
          <i class="pi pi-database" style="color: #1f5f74;"></i>
        </button>
        <button
          type="button"
          class="rail-icon-btn"
          *ngIf="selectedDataset"
          (click)="toggleLeftPane()"
          title="Data Groups ({{ dataGroups().length }})">
          <i class="pi pi-objects-column" style="color: #2563eb;"></i>
        </button>
        <button
          type="button"
          class="rail-icon-btn"
          (click)="toggleLeftPane()"
          title="Summary Functions ({{ definition.aggregates.length }})">
          <i class="pi pi-calculator" style="color: #16a34a;"></i>
        </button>
        <button
          type="button"
          class="rail-icon-btn"
          (click)="toggleLeftPane()"
          title="Saved Reports ({{ savedReports.length }})">
          <i class="pi pi-bookmark" style="color: #d97706;"></i>
        </button>
      </div>

      <!-- EXPANDED LEFT PANE CONTENT -->
      <div class="left-pane-content" *ngIf="!leftPaneCollapsed">
        <!-- DATA MODEL EXPLORER CARD -->
        <div class="panel left-tree-card">
          <div class="panel-header tree-header">
            <div>
              <h2>Data Model</h2>
              <p>Approved enterprise datasets</p>
            </div>
            <div class="header-tools">
              <span class="format-count-pill">{{ datasets.length }} Sets</span>
              <button
                type="button"
                class="panel-collapse-btn"
                (click)="toggleLeftPane()"
                title="Collapse left pane">
                <i class="pi pi-angle-double-left"></i>
              </button>
            </div>
          </div>

          <div class="tree-search-bar">
            <i class="pi pi-search search-icon"></i>
            <input
              type="text"
              [(ngModel)]="catalogSearch"
              placeholder="Search datasets, keys..."
              aria-label="Filter datasets or elements" />
            <button
              *ngIf="catalogSearch"
              type="button"
              class="clear-search-btn"
              (click)="catalogSearch = ''"
              title="Clear search">
              <i class="pi pi-times"></i>
            </button>
          </div>

          <!-- SECTION 1: DATA SETS -->
          <div class="tree-section-wrapper">
            <div class="tree-section-header" (click)="toggleTreeSection('datasets')">
              <div class="section-title-wrap">
                <i class="pi chevron-icon" [ngClass]="isTreeSectionCollapsed('datasets') ? 'pi-chevron-right' : 'pi-chevron-down'"></i>
                <div class="section-icon-badge" style="background: #e8f3f6; color: #1f5f74;">
                  <i class="pi pi-database"></i>
                </div>
                <div class="section-text">
                  <span class="section-name">Data Sets</span>
                  <small class="section-sub">Grouped by module</small>
                </div>
              </div>
              <div class="section-actions" (click)="$event.stopPropagation()">
                <button
                  type="button"
                  class="mini-action-btn"
                  (click)="toggleAllModules($event)"
                  [title]="areAllModulesCollapsed() ? 'Expand all modules' : 'Collapse all modules'">
                  <i class="pi" [ngClass]="areAllModulesCollapsed() ? 'pi-arrows-v' : 'pi-minus'"></i>
                </button>
                <span class="section-count-tag">{{ datasets.length }}</span>
              </div>
            </div>

            <div class="tree-section-body" *ngIf="!isTreeSectionCollapsed('datasets')">
              <div class="module-card-group" *ngFor="let mod of datasetModules(); trackBy: trackByModule">
                <button
                  type="button"
                  class="module-group-header"
                  [class.expanded]="!isDatasetModuleCollapsed(mod.module)"
                  (click)="toggleDatasetModule(mod.module, $event)">
                  <i class="pi chevron-icon" [ngClass]="isDatasetModuleCollapsed(mod.module) ? 'pi-chevron-right' : 'pi-chevron-down'"></i>
                  <div class="module-icon-wrap" [style.background]="moduleBg(mod.module)">
                    <i [class]="moduleIcon(mod.module)" [style.color]="moduleColor(mod.module)"></i>
                  </div>
                  <span class="module-title">{{ mod.module }}</span>
                  <span class="module-count-badge">{{ mod.datasets.length }}</span>
                </button>

                <div class="module-dataset-list" *ngIf="!isDatasetModuleCollapsed(mod.module)">
                  <button
                    type="button"
                    class="dataset-item-btn"
                    *ngFor="let ds of mod.datasets; trackBy: trackDataset"
                    [class.active]="definition.datasetKey === ds.datasetKey"
                    (click)="onDatasetChange(ds.datasetKey)">
                    <div class="dataset-item-icon" [style.background]="definition.datasetKey === ds.datasetKey ? '#1f5f74' : '#f1f5f9'">
                      <i class="pi pi-table" [style.color]="definition.datasetKey === ds.datasetKey ? '#ffffff' : '#475569'"></i>
                    </div>
                    <div class="dataset-item-text">
                      <span class="dataset-label">{{ ds.label }}</span>
                      <small class="dataset-meta">{{ ds.datasetKey }} · {{ ds.fields.length }} elements</small>
                    </div>
                    <i *ngIf="definition.datasetKey === ds.datasetKey" class="pi pi-check active-indicator"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- SECTION 2: DATA GROUPS (ACTIVE DATASET) -->
          <div class="tree-section-wrapper" *ngIf="selectedDataset">
            <div class="tree-section-header" (click)="toggleTreeSection('groups')">
              <div class="section-title-wrap">
                <i class="pi chevron-icon" [ngClass]="isTreeSectionCollapsed('groups') ? 'pi-chevron-right' : 'pi-chevron-down'"></i>
                <div class="section-icon-badge" style="background: #dbeafe; color: #2563eb;">
                  <i class="pi pi-objects-column"></i>
                </div>
                <div class="section-text">
                  <span class="section-name">Data Groups</span>
                  <small class="section-sub">{{ selectedDataset.datasetKey }}</small>
                </div>
              </div>
              <span class="section-count-tag">{{ dataGroups().length }}</span>
            </div>

            <div class="tree-section-body" *ngIf="!isTreeSectionCollapsed('groups')">
              <div class="group-items-list">
                <button
                  type="button"
                  class="group-item-btn"
                  *ngFor="let group of dataGroups(); trackBy: trackDataGroup"
                  [class.active]="activeGroupFilter === group.groupKey"
                  (click)="focusDataGroup(group.groupKey)">
                  <span class="group-key-pill">{{ group.groupKey }}</span>
                  <span class="group-item-title">{{ group.shortTitle }}</span>
                  <span class="group-ratio-badge" [class.has-selected]="group.selectedCount > 0">
                    {{ group.selectedCount }}/{{ group.totalCount }}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <!-- SECTION 3: SUMMARY FUNCTIONS -->
          <div class="tree-section-wrapper">
            <div class="tree-section-header" (click)="toggleTreeSection('summaries')">
              <div class="section-title-wrap">
                <i class="pi chevron-icon" [ngClass]="isTreeSectionCollapsed('summaries') ? 'pi-chevron-right' : 'pi-chevron-down'"></i>
                <div class="section-icon-badge" style="background: #dcfce7; color: #16a34a;">
                  <i class="pi pi-calculator"></i>
                </div>
                <div class="section-text">
                  <span class="section-name">Summary Functions</span>
                  <small class="section-sub">Calculated aggregates</small>
                </div>
              </div>
              <span class="section-count-tag">{{ definition.aggregates.length }}</span>
            </div>

            <div class="tree-section-body" *ngIf="!isTreeSectionCollapsed('summaries')">
              <div class="summary-items-list" *ngIf="definition.aggregates.length">
                <div class="summary-item-row" *ngFor="let agg of definition.aggregates; let i = index">
                  <span class="summary-fn-badge">{{ agg.function }}</span>
                  <span class="summary-field-name">{{ fieldLabel(agg.fieldKey) }}</span>
                  <button type="button" class="summary-remove-btn" (click)="removeAggregate(i)" title="Remove summary">×</button>
                </div>
              </div>
              <div *ngIf="!definition.aggregates.length" class="tree-empty-tip">
                <i class="pi pi-info-circle"></i>
                <span>Drag any numeric element to a group's summary zone.</span>
              </div>
            </div>
          </div>

          <!-- SECTION 4: SAVED REPORTS -->
          <div class="tree-section-wrapper">
            <div class="tree-section-header" (click)="toggleTreeSection('saved')">
              <div class="section-title-wrap">
                <i class="pi chevron-icon" [ngClass]="isTreeSectionCollapsed('saved') ? 'pi-chevron-right' : 'pi-chevron-down'"></i>
                <div class="section-icon-badge" style="background: #fef3c7; color: #d97706;">
                  <i class="pi pi-bookmark"></i>
                </div>
                <div class="section-text">
                  <span class="section-name">Saved Reports</span>
                  <small class="section-sub">Saved report catalog</small>
                </div>
              </div>
              <span class="section-count-tag">{{ savedReports.length }}</span>
            </div>

            <div class="tree-section-body" *ngIf="!isTreeSectionCollapsed('saved')">
              <div class="saved-reports-list" *ngIf="savedReports.length">
                <button
                  type="button"
                  class="saved-report-btn"
                  *ngFor="let report of savedReports"
                  (click)="openReport(report)"
                  [class.active]="definition.reportId === report.reportId">
                  <div class="saved-report-icon">
                    <i class="pi pi-file"></i>
                  </div>
                  <div class="saved-report-text">
                    <span class="saved-report-name">{{ report.reportName }}</span>
                    <small class="saved-report-meta">{{ report.datasetKey }} · v{{ report.version || 1 }}</small>
                  </div>
                  <i *ngIf="definition.reportId === report.reportId" class="pi pi-check active-indicator"></i>
                </button>
              </div>
              <div *ngIf="!savedReports.length" class="tree-empty-tip">
                <span>No saved reports yet.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>

    <main class="designer-panel">
      <section class="panel">
        <div class="panel-header">
          <div>
            <h2>Definition</h2>
            <p>Choose the approved ERP dataset and report layout identity.</p>
          </div>
          <div class="bi-definition-badges" *ngIf="selectedDataset">
            <p-tag [value]="selectedDataset.module" severity="secondary"></p-tag>
            <p-tag [value]="dataGroups().length + ' Data Groups'" severity="info"></p-tag>
          </div>
        </div>

        <div class="form-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input id="reportName" pInputText [(ngModel)]="definition.reportName" (ngModelChange)="definition.branding.title = $event" />
              <label for="reportName">Report Name</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-database"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select
                inputId="datasetKey"
                [(ngModel)]="definition.datasetKey"
                [options]="datasets"
                optionLabel="label"
                optionValue="datasetKey"
                [filter]="true"
                filterBy="label,module,description,datasetKey"
                filterPlaceholder="Search 41 core datasets"
                emptyFilterMessage="No matching datasets"
                appendTo="body"
                (ngModelChange)="onDatasetChange($event)">
                <ng-template #selectedItem let-dataset>
                  <span *ngIf="dataset">{{ dataset.label }} ({{ dataset.module }})</span>
                </ng-template>
                <ng-template #item let-dataset>
                  <div class="dataset-option">
                    <strong>{{ dataset.label }}</strong>
                    <small>{{ dataset.module }} · {{ dataset.description }}</small>
                  </div>
                </ng-template>
              </p-select>
              <label for="datasetKey">Dataset</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-clone"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="pageSize" [(ngModel)]="definition.layout.pageSize" [options]="pageSizes" appendTo="body"></p-select>
              <label for="pageSize">Page Size</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-arrows-h"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="orientation" [(ngModel)]="definition.layout.orientation" [options]="orientations" appendTo="body"></p-select>
              <label for="orientation">Orientation</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>

        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input id="reportSubtitle" pInputText [(ngModel)]="definition.branding.subtitle" />
            <label for="reportSubtitle">Subtitle</label>
          </p-iftalabel>
        </p-inputgroup>
      </section>

      <section class="panel bi-datamodel-panel">
        <div class="bi-datamodel-topbar">
          <div class="bi-datamodel-title">
            <div class="bi-mode-tabs" role="tablist" aria-label="Oracle BI Data Model views">
              <button
                type="button"
                class="bi-mode-tab"
                [class.active]="dataModelViewMode === 'diagram'"
                (click)="dataModelViewMode = 'diagram'">
                <i class="pi pi-sitemap"></i>
                <span>Diagram</span>
              </button>
              <button
                type="button"
                class="bi-mode-tab"
                [class.active]="dataModelViewMode === 'structure'"
                (click)="dataModelViewMode = 'structure'">
                <i class="pi pi-table"></i>
                <span>Structure</span>
              </button>
              <button
                type="button"
                class="bi-mode-tab"
                [class.active]="dataModelViewMode === 'code'"
                (click)="dataModelViewMode = 'code'">
                <i class="pi pi-code"></i>
                <span>Code</span>
              </button>
            </div>
            <div class="bi-dataset-summary" *ngIf="selectedDataset">
              <div class="bi-dataset-summary-badge">
                <i class="pi pi-database"></i>
              </div>
              <div class="bi-dataset-summary-text">
                <strong>{{ selectedDataset.label }}</strong>
                <span>{{ selectedDataset.description }}</span>
              </div>
            </div>
          </div>

          <div class="bi-datamodel-actions">
            <p-tag [value]="selectedFieldKeys.length + ' columns'" severity="info"></p-tag>
            <p-tag *ngIf="definition.aggregates.length" [value]="definition.aggregates.length + ' summaries'" severity="success"></p-tag>
            <button pButton type="button" size="small" icon="pi pi-star" label="Recommended" [outlined]="true" (click)="selectRecommendedColumns()"></button>
            <button pButton type="button" size="small" icon="pi pi-check-square" label="All" [outlined]="true" (click)="selectAllColumns()"></button>
            <button pButton type="button" size="small" icon="pi pi-times" label="Clear" [outlined]="true" severity="secondary" (click)="clearAllColumns()"></button>
          </div>
        </div>

        <div class="bi-toolbar-row">
          <p-inputgroup class="bi-element-search">
            <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input id="fieldSearch" pInputText [(ngModel)]="fieldSearch" />
              <label for="fieldSearch">Search Data Elements (e.g. SHIPMENT_NUMBER, customer, amount)</label>
            </p-iftalabel>
          </p-inputgroup>

          <div class="bi-group-filter-pills">
            <button
              type="button"
              class="bi-group-pill"
              [class.active]="activeGroupFilter === 'ALL'"
              (click)="activeGroupFilter = 'ALL'">
              All Groups ({{ dataGroups().length }})
            </button>
            <button
              type="button"
              class="bi-group-pill"
              *ngFor="let group of dataGroups(); trackBy: trackDataGroup"
              [class.active]="activeGroupFilter === group.groupKey"
              (click)="focusDataGroup(group.groupKey)">
              <strong>{{ group.groupKey }}</strong>
              <span>{{ group.shortTitle }}</span>
              <small>{{ group.selectedCount }}/{{ group.totalCount }}</small>
            </button>
          </div>
        </div>

        <!-- ORACLE FUSION BI DIAGRAM VIEW -->
        <div class="bi-diagram-canvas" *ngIf="dataModelViewMode === 'diagram'">
          <div class="bi-query-root-node" *ngIf="selectedDataset">
            <div class="bi-query-root-header">
              <div class="query-root-icon-badge">
                <i class="pi pi-database"></i>
              </div>
              <div class="query-root-identity">
                <div class="query-root-title-row">
                  <span class="query-root-label">Data Set Query Root</span>
                  <code class="query-root-name">{{ selectedDataset.datasetKey | uppercase }}_Q1</code>
                </div>
                <div class="query-root-tags">
                  <span class="bi-query-root-type">
                    <i class="pi pi-box"></i>
                    ERP Data Set · {{ selectedDataset.module }}
                  </span>
                  <span class="bi-query-root-meta">
                    <i class="pi pi-table"></i>
                    {{ selectedDataset.fields.length }} Data Elements &rarr; {{ dataGroups().length }} Groups
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="bi-groups-grid">
            <article
              class="bi-group-card"
              [class.has-selected]="group.selectedCount > 0"
              *ngFor="let group of visibleDataGroups(); trackBy: trackDataGroup">
              <header class="bi-group-card-header">
                <button
                  type="button"
                  class="bi-group-collapse-btn"
                  (click)="toggleDataGroupCollapsed(group.groupKey, $event)"
                  [attr.aria-label]="'Toggle ' + group.groupKey">
                  <i class="pi" [ngClass]="isDataGroupCollapsed(group.groupKey) ? 'pi-chevron-right' : 'pi-chevron-down'"></i>
                </button>
                <div class="bi-group-card-title" (click)="toggleDataGroupCollapsed(group.groupKey, $event)">
                  <span class="bi-group-key-badge">{{ group.groupKey }}</span>
                  <span class="bi-group-name-text">{{ group.shortTitle }}</span>
                </div>
                <div class="bi-group-card-controls">
                  <span class="bi-group-count-pill" [class.has-selected]="group.selectedCount > 0">
                    {{ group.selectedCount }}/{{ group.totalCount }}
                  </span>
                  <input
                    type="checkbox"
                    class="bi-group-master-checkbox"
                    [checked]="group.selectedCount === group.totalCount && group.totalCount > 0"
                    [indeterminate]="group.selectedCount > 0 && group.selectedCount < group.totalCount"
                    (change)="toggleGroupSelection(group, $any($event.target).checked, $event)"
                    title="Select or deselect all elements in {{ group.groupKey }}" />
                </div>
              </header>

              <div class="bi-group-elements" *ngIf="!isDataGroupCollapsed(group.groupKey)">
                <div
                  class="bi-element-row"
                  *ngFor="let field of group.fields; trackBy: trackField"
                  [class.selected]="isColumnSelected(field.fieldKey)"
                  draggable="true"
                  (dragstart)="onFieldDragStart(field, $event)"
                  (dragend)="onFieldDragEnd()">
                  <input
                    type="checkbox"
                    class="bi-element-checkbox"
                    [checked]="isColumnSelected(field.fieldKey)"
                    (change)="toggleColumn(field, $any($event.target).checked)" />

                  <span class="bi-datatype-icon" [ngClass]="dataTypeClass(field)" [title]="field.type">
                    {{ dataTypeBadge(field) }}
                  </span>

                  <div class="bi-element-identity" (click)="toggleColumn(field, !isColumnSelected(field.fieldKey))">
                    <div class="bi-element-primary">
                      <code class="bi-element-xml">{{ elementName(field) }}</code>
                      <i *ngIf="field.defaultSelected" class="pi pi-star-fill bi-recommended-star" title="Recommended core element"></i>
                    </div>
                    <span class="bi-element-label">{{ field.label }}</span>
                  </div>

                  <div class="bi-element-badges">
                    <span *ngIf="isFieldFiltered(field.fieldKey)" class="bi-state-pill filter" title="Filtered">F</span>
                    <span *ngIf="fieldSortDirection(field.fieldKey)" class="bi-state-pill sort" title="Sorted">
                      {{ fieldSortDirection(field.fieldKey) === 'ASC' ? '↑' : '↓' }}
                    </span>
                    <span *ngIf="fieldAggregates(field.fieldKey).length" class="bi-state-pill agg" title="Aggregated">
                      Σ
                    </span>
                  </div>

                  <div class="bi-element-quick-actions">
                    <button
                      type="button"
                      class="bi-quick-btn"
                      *ngIf="field.filterable"
                      (click)="quickFilterField(field, $event)"
                      title="Add filter on {{ field.label }}">
                      <i class="pi pi-filter"></i>
                    </button>
                    <button
                      type="button"
                      class="bi-quick-btn"
                      *ngIf="field.sortable"
                      (click)="quickToggleSort(field, $event)"
                      title="Toggle sort on {{ field.label }}">
                      <i class="pi pi-sort-alt"></i>
                    </button>
                    <button
                      type="button"
                      class="bi-quick-btn bi-quick-agg"
                      (click)="quickAddFieldAggregate(field, undefined, $event)"
                      title="Add summary function for {{ elementName(field) }}">
                      <span>+Σ</span>
                    </button>
                  </div>
                </div>
              </div>

              <footer
                class="bi-group-summary-zone"
                [class.drag-over]="dragOverGroupKey === group.groupKey"
                (dragover)="onGroupDropZoneDragOver(group.groupKey, $event)"
                (dragleave)="onGroupDropZoneDragLeave(group.groupKey)"
                (drop)="onGroupDropZoneDrop(group, $event)">
                <div class="bi-summary-items" *ngIf="group.aggregates.length">
                  <div class="bi-summary-chip" *ngFor="let agg of group.aggregates">
                    <i class="pi pi-calculator"></i>
                    <select
                      class="bi-summary-fn-select"
                      [ngModel]="agg.function"
                      (ngModelChange)="changeAggregateFunction(agg, $event)"
                      aria-label="Summary function">
                      <option *ngFor="let fn of aggregateFunctions" [value]="fn">{{ fn }}</option>
                    </select>
                    <span class="bi-summary-chip-label" [title]="fieldLabel(agg.fieldKey)">
                      {{ fieldLabel(agg.fieldKey) }}
                    </span>
                    <button
                      type="button"
                      class="bi-summary-chip-remove"
                      (click)="removeGroupAggregate(agg, $event)"
                      title="Remove aggregate function">
                      <i class="pi pi-times"></i>
                    </button>
                  </div>
                </div>
                <div class="bi-drop-prompt" (click)="addGroupDefaultSummary(group, $event)">
                  <i class="pi pi-plus-circle"></i>
                  <span>Drop elements here or click to add aggregate</span>
                </div>
              </footer>
            </article>
          </div>

          <!-- Selected Columns Ordering Strip -->
          <div class="bi-selected-strip">
            <div class="bi-selected-strip-header">
              <div class="bi-selected-strip-titles">
                <div class="bi-strip-heading-row">
                  <i class="pi pi-sliders-h"></i>
                  <span class="sub-panel-title">Report Output Column Order</span>
                  <p-tag [value]="selectedColumns().length + ' selected'" severity="info"></p-tag>
                </div>
                <small>Drag or use arrows to adjust sequence in final generated report table</small>
              </div>
              <button
                pButton
                *ngIf="selectedColumns().length"
                type="button"
                size="small"
                icon="pi pi-trash"
                label="Clear All"
                [text]="true"
                severity="danger"
                (click)="clearAllColumns()">
              </button>
            </div>
            <div class="bi-selected-chips" *ngIf="selectedColumns().length; else noSelectedCols">
              <div class="bi-selected-chip" *ngFor="let column of selectedColumns(); let i = index; trackBy: trackColumn">
                <span class="bi-selected-index">{{ i + 1 }}</span>
                <div class="bi-selected-chip-info">
                  <strong>{{ column.label || fieldLabel(column.fieldKey) }}</strong>
                  <code class="bi-selected-chip-code">{{ column.fieldKey }}</code>
                </div>
                <div class="bi-selected-chip-actions">
                  <button type="button" [disabled]="i === 0" (click)="moveColumn(column.fieldKey, -1)" title="Move left">
                    <i class="pi pi-arrow-left"></i>
                  </button>
                  <button type="button" [disabled]="i === selectedColumns().length - 1" (click)="moveColumn(column.fieldKey, 1)" title="Move right">
                    <i class="pi pi-arrow-right"></i>
                  </button>
                  <button type="button" class="danger" (click)="removeColumn(column.fieldKey)" title="Remove column">
                    <i class="pi pi-times"></i>
                  </button>
                </div>
              </div>
            </div>
            <ng-template #noSelectedCols>
              <div class="empty-state bi-strip-empty">
                <i class="pi pi-info-circle"></i>
                <span>No output columns selected. Check elements in the Data Group cards above.</span>
              </div>
            </ng-template>
          </div>
        </div>

        <!-- ORACLE FUSION BI STRUCTURE VIEW -->
        <div class="bi-structure-view" *ngIf="dataModelViewMode === 'structure'">
          <div class="bi-structure-table-wrap">
            <table class="bi-structure-table">
              <thead>
                <tr>
                  <th>Include</th>
                  <th>Group</th>
                  <th>XML Tag / Element Name</th>
                  <th>Business Display Label</th>
                  <th>Data Type</th>
                  <th>Source Path</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let field of structureFields(); trackBy: trackField"
                  [class.selected-row]="isColumnSelected(field.fieldKey)">
                  <td>
                    <input
                      type="checkbox"
                      [checked]="isColumnSelected(field.fieldKey)"
                      (change)="toggleColumn(field, $any($event.target).checked)" />
                  </td>
                  <td>
                    <span class="bi-group-tag">{{ fieldGroupKey(field) }}</span>
                  </td>
                  <td>
                    <code class="bi-element-xml">{{ elementName(field) }}</code>
                  </td>
                  <td>
                    <input
                      *ngIf="isColumnSelected(field.fieldKey); else staticLabel"
                      type="text"
                      class="bi-inline-label-input"
                      [ngModel]="columnLabel(field.fieldKey)"
                      (ngModelChange)="updateColumnLabel(field.fieldKey, $event)" />
                    <ng-template #staticLabel>
                      <span>{{ field.label }}</span>
                    </ng-template>
                  </td>
                  <td>
                    <span class="bi-datatype-icon" [ngClass]="dataTypeClass(field)">
                      {{ dataTypeBadge(field) }} · {{ field.type }}
                    </span>
                  </td>
                  <td>
                    <small class="bi-source-path">{{ fieldPath(field) }}</small>
                  </td>
                  <td>
                    <div class="bi-structure-actions">
                      <button
                        type="button"
                        class="bi-quick-btn"
                        *ngIf="field.filterable"
                        (click)="quickFilterField(field, $event)"
                        title="Filter">
                        <i class="pi pi-filter"></i>
                      </button>
                      <button
                        type="button"
                        class="bi-quick-btn"
                        *ngIf="field.sortable"
                        (click)="quickToggleSort(field, $event)"
                        title="Sort">
                        <i class="pi pi-sort-alt"></i>
                      </button>
                      <button
                        type="button"
                        class="bi-quick-btn bi-quick-agg"
                        (click)="quickAddFieldAggregate(field, undefined, $event)"
                        title="Add summary function">
                        Σ
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ORACLE FUSION BI CODE VIEW -->
        <div class="bi-code-view" *ngIf="dataModelViewMode === 'code'">
          <pre class="bi-code-pre"><code>{{ dataModelCodePreview() }}</code></pre>
        </div>
      </section>

      <section class="panel">
        <div class="panel-header">
          <div>
            <h2>Filters, Sorts, And Totals</h2>
            <p>Build report rules one step at a time; every choice is validated on preview.</p>
          </div>
        </div>

        <div class="rule-tabs" role="tablist" aria-label="Report rules">
          <button type="button" [class.active]="activeRuleTab === 'filters'" (click)="activeRuleTab = 'filters'">
            <i class="pi pi-filter"></i>
            <span>Filters</span>
            <strong>{{ definition.filters.length }}</strong>
          </button>
          <button type="button" [class.active]="activeRuleTab === 'sorts'" (click)="activeRuleTab = 'sorts'">
            <i class="pi pi-sort-alt"></i>
            <span>Sorts</span>
            <strong>{{ definition.sorts.length }}</strong>
          </button>
          <button type="button" [class.active]="activeRuleTab === 'totals'" (click)="activeRuleTab = 'totals'">
            <i class="pi pi-calculator"></i>
            <span>Totals</span>
            <strong>{{ definition.aggregates.length }}</strong>
          </button>
        </div>

        <div class="rule-panel" *ngIf="activeRuleTab === 'filters'">
          <div class="rule-editor-row filter-rule-row">
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-filter"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="filterField" [ngModel]="newFilter.fieldKey" (ngModelChange)="onFilterFieldChange($event)" [options]="filterableFields()" optionLabel="label" optionValue="fieldKey" appendTo="body"></p-select>
                <label for="filterField">Field</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-code"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="filterOperator" [(ngModel)]="newFilter.operator" [options]="availableFilterOperators()" optionLabel="label" optionValue="value" appendTo="body"></p-select>
                <label for="filterOperator">Operator</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i [class]="filterValueType() === 'date' ? 'pi pi-calendar-clock' : (filterValueType() === 'number' ? 'pi pi-hashtag' : 'pi pi-pencil')"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-datepicker *ngIf="filterValueType() === 'date'" inputId="filterValue" [(ngModel)]="newFilter.value" [showTime]="true" hourFormat="24" [showIcon]="true" appendTo="body" styleClass="w-full"></p-datepicker>
                <p-inputNumber *ngIf="filterValueType() === 'number'" inputId="filterValue" [(ngModel)]="newFilter.value" mode="decimal" [maxFractionDigits]="20" [useGrouping]="false" styleClass="w-full"></p-inputNumber>
                <input *ngIf="filterValueType() === 'text'" id="filterValue" pInputText [(ngModel)]="newFilter.value" />
                <label for="filterValue">Value</label>
              </p-iftalabel>
            </p-inputgroup>
            <button pButton type="button" icon="pi pi-plus" label="Add" [outlined]="true" (click)="addFilter()"></button>
          </div>
          <div class="rule-list" *ngIf="definition.filters.length; else noFilters">
            <button type="button" class="rule-item" *ngFor="let filter of definition.filters; let i = index" (click)="removeFilter(i)">
              <i class="pi pi-filter"></i>
              <span><strong>{{ fieldLabel(filter.fieldKey) }}</strong><small>{{ filter.operator }} {{ filter.value }}</small></span>
              <i class="pi pi-times remove-icon"></i>
            </button>
          </div>
          <ng-template #noFilters><div class="empty-state rule-empty">No filters added.</div></ng-template>
        </div>

        <div class="rule-panel" *ngIf="activeRuleTab === 'sorts'">
          <div class="rule-editor-row sort-rule-row">
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-sort-alt"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="sortField" [(ngModel)]="newSort.fieldKey" [options]="sortableFields()" optionLabel="label" optionValue="fieldKey" appendTo="body"></p-select>
                <label for="sortField">Field</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-sort-amount-down"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="sortDirection" [(ngModel)]="newSort.direction" [options]="sortDirections" optionLabel="label" optionValue="value" appendTo="body"></p-select>
                <label for="sortDirection">Direction</label>
              </p-iftalabel>
            </p-inputgroup>
            <button pButton type="button" icon="pi pi-plus" label="Add" [outlined]="true" (click)="addSort()"></button>
          </div>
          <div class="rule-list" *ngIf="definition.sorts.length; else noSorts">
            <button type="button" class="rule-item" *ngFor="let sort of definition.sorts" (click)="removeSort(sort.fieldKey)">
              <i class="pi pi-sort-alt"></i>
              <span><strong>{{ fieldLabel(sort.fieldKey) }}</strong><small>{{ sort.direction === 'ASC' ? 'Ascending' : 'Descending' }}</small></span>
              <i class="pi pi-times remove-icon"></i>
            </button>
          </div>
          <ng-template #noSorts><div class="empty-state rule-empty">No sorts added.</div></ng-template>
        </div>

        <div class="rule-panel" *ngIf="activeRuleTab === 'totals'">
          <div class="rule-editor-row total-rule-row">
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="aggregateField" [(ngModel)]="newAggregateFieldKey" [options]="aggregatableFields()" optionLabel="label" optionValue="fieldKey" appendTo="body" (ngModelChange)="onAggregateFieldChange()"></p-select>
                <label for="aggregateField">Field</label>
              </p-iftalabel>
            </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-calculator"></i></p-inputgroup-addon>
              <p-iftalabel>
                <p-select inputId="aggregateFunction" [(ngModel)]="newAggregateFunction" [options]="aggregateFunctionOptions()" optionLabel="label" optionValue="value" appendTo="body"></p-select>
                <label for="aggregateFunction">Total</label>
              </p-iftalabel>
            </p-inputgroup>
            <button pButton type="button" icon="pi pi-plus" label="Add" [outlined]="true" (click)="addAggregate()"></button>
          </div>
          <div class="rule-list" *ngIf="definition.aggregates.length; else noTotals">
            <button type="button" class="rule-item" *ngFor="let aggregate of definition.aggregates; let i = index" (click)="removeAggregate(i)">
              <i class="pi pi-calculator"></i>
              <span><strong>{{ aggregate.function }} {{ fieldLabel(aggregate.fieldKey) }}</strong><small>{{ aggregateLabel(aggregate) }}</small></span>
              <i class="pi pi-times remove-icon"></i>
            </button>
          </div>
          <ng-template #noTotals><div class="empty-state rule-empty">No totals added.</div></ng-template>
        </div>

        <div class="builder-row" *ngIf="false">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-filter"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select
                inputId="filterField"
                [(ngModel)]="newFilter.fieldKey"
                [options]="filterableFields()"
                optionLabel="label"
                optionValue="fieldKey"
                appendTo="body">
              </p-select>
              <label for="filterField">Filter Field</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-code"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select
                inputId="filterOperator"
                [(ngModel)]="newFilter.operator"
                [options]="filterOperators"
                optionLabel="label"
                optionValue="value"
                appendTo="body">
              </p-select>
              <label for="filterOperator">Operator</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-pencil"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input id="filterValue" pInputText [(ngModel)]="newFilter.value" />
              <label for="filterValue">Value</label>
            </p-iftalabel>
          </p-inputgroup>
          <button pButton type="button" icon="pi pi-filter" label="Add Filter" [outlined]="true" (click)="addFilter()"></button>
        </div>

        <div class="chip-row" *ngIf="false">
          <button type="button" class="pill" *ngFor="let filter of definition.filters; let i = index" (click)="removeFilter(i)">
            {{ fieldLabel(filter.fieldKey) }} {{ filter.operator }} {{ filter.value }} ×
          </button>
        </div>

        <div class="builder-row" *ngIf="false">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-sort-alt"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select
                inputId="sortField"
                [(ngModel)]="newSort.fieldKey"
                [options]="sortableFields()"
                optionLabel="label"
                optionValue="fieldKey"
                appendTo="body">
              </p-select>
              <label for="sortField">Sort Field</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-sort-amount-down"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select
                inputId="sortDirection"
                [(ngModel)]="newSort.direction"
                [options]="sortDirections"
                optionLabel="label"
                optionValue="value"
                appendTo="body">
              </p-select>
              <label for="sortDirection">Direction</label>
            </p-iftalabel>
          </p-inputgroup>
          <button pButton type="button" icon="pi pi-sort-alt" label="Add Sort" [outlined]="true" (click)="addSort()"></button>
        </div>

        <div class="chip-row" *ngIf="false">
          <button type="button" class="pill" *ngFor="let sort of definition.sorts" (click)="removeSort(sort.fieldKey)">
            {{ fieldLabel(sort.fieldKey) }} {{ sort.direction }} ×
          </button>
        </div>

        <div class="aggregate-grid" *ngIf="false">
          <ng-container *ngFor="let field of aggregatableFields()">
            <label *ngFor="let aggregation of field.aggregations" class="field-option compact">
              <input type="checkbox" [checked]="hasAggregate(field.fieldKey, aggregation)" (change)="toggleAggregate(field, aggregation, $any($event.target).checked)" />
              <span><strong>{{ aggregation }}</strong><small>{{ field.label }}</small></span>
            </label>
          </ng-container>
        </div>
      </section>
    </main>

    <aside class="export-panel" [class.collapsed]="rightPaneCollapsed">
      <!-- COLLAPSED RAIL -->
      <div class="right-pane-rail" *ngIf="rightPaneCollapsed">
        <button
          type="button"
          class="rail-toggle-btn"
          (click)="toggleRightPane()"
          title="Expand Inspector & Exports">
          <i class="pi pi-angle-double-left"></i>
        </button>
        <div class="rail-divider"></div>
        <button
          type="button"
          class="rail-icon-btn"
          *ngFor="let format of formats"
          [title]="'Export as ' + format.toUpperCase()"
          [disabled]="reportActions.isPending('export:' + format)"
          (click)="export(format)">
          <i [class]="formatIcon(format)" [style.color]="formatColor(format)"></i>
        </button>
        <div class="rail-divider"></div>
        <button
          type="button"
          class="rail-icon-btn preview-rail-btn"
          title="Run Preview"
          [disabled]="loading"
          (click)="previewReport()">
          <i class="pi pi-eye"></i>
        </button>
      </div>

      <!-- EXPANDED RIGHT PANE CONTENT -->
      <div class="right-pane-content" *ngIf="!rightPaneCollapsed">
        <!-- REPORT INSPECTOR / SUMMARY CARD -->
        <div class="panel inspector-card">
          <div class="panel-header inspector-header">
            <div>
              <h2>Inspector</h2>
              <p>Report overview</p>
            </div>
            <div class="header-tools">
              <span class="inspector-badge" [class.ready]="selectedFieldKeys.length > 0">
                <i class="pi" [ngClass]="selectedFieldKeys.length > 0 ? 'pi-check-circle' : 'pi-info-circle'"></i>
                {{ selectedFieldKeys.length > 0 ? 'Ready' : 'Draft' }}
              </span>
              <button
                type="button"
                class="panel-collapse-btn"
                (click)="toggleRightPane()"
                title="Collapse right pane">
                <i class="pi pi-angle-double-right"></i>
              </button>
            </div>
          </div>

          <div class="inspector-stats">
            <div class="stat-row">
              <span class="stat-label"><i class="pi pi-database"></i> Dataset</span>
              <span class="stat-value" [title]="selectedDataset?.label">{{ selectedDataset?.label || 'None' }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label"><i class="pi pi-list"></i> Columns</span>
              <span class="stat-value highlight">{{ selectedFieldKeys.length }} selected</span>
            </div>
            <div class="stat-row">
              <span class="stat-label"><i class="pi pi-th-large"></i> Groups</span>
              <span class="stat-value">{{ dataGroups().length }} active</span>
            </div>
            <div class="stat-row">
              <span class="stat-label"><i class="pi pi-filter"></i> Rules</span>
              <span class="stat-value">{{ definition.filters.length }}F · {{ definition.sorts.length }}S · {{ definition.aggregates.length }}T</span>
            </div>
            <div class="stat-row">
              <span class="stat-label"><i class="pi pi-clone"></i> Layout</span>
              <span class="stat-value">{{ definition.layout.pageSize }} · {{ definition.layout.orientation }}</span>
            </div>
          </div>

          <div class="inspector-actions">
            <button
              pButton
              type="button"
              size="small"
              icon="pi pi-eye"
              label="Run Preview"
              [loading]="loading"
              class="w-full inspector-preview-btn"
              (click)="previewReport()">
            </button>
          </div>
        </div>

        <!-- EXPORTS CARD -->
        <div class="panel export-card">
          <div class="panel-header export-header">
            <div>
              <h2>Exports</h2>
              <p>Download report formats</p>
            </div>
            <span class="format-count-pill">{{ formats.length }} Formats</span>
          </div>

          <div class="export-format-list">
            <button
              type="button"
              class="export-format-btn"
              *ngFor="let format of formats"
              [class.loading]="reportActions.isPending('export:' + format)"
              [disabled]="reportActions.isPending('export:' + format)"
              (click)="export(format)">
              <div class="format-icon-wrap" [style.background]="formatBg(format)">
                <i [class]="formatIcon(format)" [style.color]="formatColor(format)"></i>
              </div>
              <div class="format-info">
                <span class="format-name">{{ formatTitle(format) }}</span>
                <small class="format-desc">{{ formatDesc(format) }}</small>
              </div>
              <div class="format-status">
                <i *ngIf="reportActions.isPending('export:' + format)" class="pi pi-spin pi-spinner format-spinner"></i>
                <span *ngIf="!reportActions.isPending('export:' + format)" class="format-ext-pill" [style.color]="formatColor(format)">.{{ format }}</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </aside>
  </section>

  <section class="panel preview-panel" *ngIf="activeWorkspace === 'builder'">
    <div class="panel-header">
      <div>
        <h2>Preview</h2>
        <p>{{ preview?.result?.totalRows || 0 }} rows matched; preview shows the configured columns.</p>
      </div>
      <div class="header-actions" *ngIf="preview">
        <button
          pButton
          type="button"
          *ngFor="let format of formats"
          [icon]="formatIcon(format)"
          [label]="format.toUpperCase()"
          [outlined]="true"
          size="small"
          [loading]="reportActions.isPending('export:' + format)"
          (click)="export(format)">
        </button>
      </div>
    </div>

    <p-table *ngIf="preview" [value]="preview.result.rows" styleClass="p-datatable-sm" [paginator]="true" [rows]="10">
      <ng-template pTemplate="header">
        <tr>
          <th *ngFor="let column of preview.result.columns; trackBy: trackColumn">{{ column.label || column.fieldKey }}</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td *ngFor="let column of preview.result.columns; trackBy: trackColumn">
            <p-tag
              *ngIf="column.fieldKey.toLowerCase().includes('status'); else normalCell"
              [value]="cell(row, column.fieldKey)"
              [severity]="severity(row[column.fieldKey])">
            </p-tag>
            <ng-template #normalCell>{{ cell(row, column.fieldKey) }}</ng-template>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="footer" *ngIf="definition.layout.showTotals">
        <tr>
          <td *ngFor="let column of preview.result.columns; trackBy: trackColumn">
            <strong>{{ totalCell(column.fieldKey) }}</strong>
          </td>
        </tr>
      </ng-template>
    </p-table>

    <div *ngIf="!preview" class="empty-state large">Configure a report and click Preview.</div>
  </section>

  <section class="sql-workspace" [class.right-collapsed]="rightPaneCollapsed" [class.left-collapsed]="leftPaneCollapsed" *ngIf="activeWorkspace === 'sql'">
    <!-- LEFT PANE: SAVED SQL MODELS -->
    <aside class="catalog-panel sql-models-panel" [class.collapsed]="leftPaneCollapsed">
      <!-- COLLAPSED RAIL -->
      <div class="left-pane-rail" *ngIf="leftPaneCollapsed">
        <button
          type="button"
          class="rail-toggle-btn"
          (click)="toggleLeftPane()"
          title="Expand SQL Models">
          <i class="pi pi-angle-double-right"></i>
        </button>
        <div class="rail-divider"></div>
        <button
          type="button"
          class="rail-icon-btn"
          (click)="toggleLeftPane()"
          title="SQL Models ({{ sqlQueries.length }})">
          <i class="pi pi-database" style="color: #1f5f74;"></i>
        </button>
      </div>

      <!-- EXPANDED CONTENT -->
      <div class="left-pane-content" *ngIf="!leftPaneCollapsed">
        <div class="panel sql-models-card">
          <div class="panel-header tree-header">
            <div>
              <h2>SQL Models</h2>
              <p>Saved virtual queries</p>
            </div>
            <div class="header-tools">
              <span class="format-count-pill">{{ sqlQueries.length }} Models</span>
              <button
                type="button"
                class="panel-collapse-btn"
                (click)="toggleLeftPane()"
                title="Collapse left pane">
                <i class="pi pi-angle-double-left"></i>
              </button>
            </div>
          </div>

          <div class="tree-search-bar" *ngIf="sqlQueries.length">
            <i class="pi pi-search search-icon"></i>
            <input
              type="text"
              [(ngModel)]="sqlModelSearch"
              placeholder="Search SQL models..."
              aria-label="Filter saved SQL queries" />
            <button
              *ngIf="sqlModelSearch"
              type="button"
              class="clear-search-btn"
              (click)="sqlModelSearch = ''"
              title="Clear search">
              <i class="pi pi-times"></i>
            </button>
          </div>

          <div class="sql-models-list" *ngIf="filteredSqlQueries().length">
            <button
              type="button"
              class="sql-model-item"
              *ngFor="let query of filteredSqlQueries()"
              (click)="openSqlQuery(query)"
              [class.active]="selectedSqlQuery?.queryId === query.queryId">
              <div class="sql-model-icon" [style.background]="selectedSqlQuery?.queryId === query.queryId ? '#0f2942' : '#f1f5f9'">
                <i class="pi pi-database" [style.color]="selectedSqlQuery?.queryId === query.queryId ? '#ffffff' : '#0284c7'"></i>
              </div>
              <div class="sql-model-info">
                <span class="sql-model-name">{{ query.queryName }}</span>
                <div class="sql-model-meta-row">
                  <span class="sql-version-tag">v{{ query.version || 1 }}</span>
                  <span class="sql-status-tag" [class.published]="query.published">
                    {{ query.published ? 'PUBLISHED' : 'DRAFT' }}
                  </span>
                  <small class="sql-source-tag" *ngIf="query.sourceName">{{ query.sourceName }}</small>
                </div>
              </div>
              <i *ngIf="selectedSqlQuery?.queryId === query.queryId" class="pi pi-check active-indicator"></i>
            </button>
          </div>

          <div *ngIf="!sqlQueries.length" class="sql-empty-state">
            <div class="empty-icon-wrap">
              <i class="pi pi-database"></i>
            </div>
            <strong>No SQL Data Models Yet</strong>
            <p>Write custom queries to join approved sources, then publish as virtual data sets.</p>
            <button
              pButton
              type="button"
              size="small"
              icon="pi pi-plus"
              label="New SQL Model"
              [disabled]="!canEditSql"
              (click)="newSqlQuery()">
            </button>
          </div>
        </div>
      </div>
    </aside>

    <!-- MIDDLE PANE: DESIGNER & PREVIEW -->
    <main class="designer-panel sql-designer-panel">
      <!-- SECURITY CALLOUT -->
      <div class="security-callout modern-callout">
        <div class="security-callout-icon">
          <i class="pi pi-shield"></i>
        </div>
        <div class="security-callout-content">
          <div class="security-callout-title">
            <strong>Tenant Isolation Enforced by Database</strong>
            <span class="security-badge">READ-ONLY</span>
          </div>
          <p>Only PostgreSQL SELECT is permitted. Every query is tenant-filtered, permission-checked, read-only, row-limited (max 500), time-limited, and audited.</p>
        </div>
      </div>

      <div *ngIf="sqlErrorMessage" class="error-panel">{{ sqlErrorMessage }}</div>

      <!-- SQL DATA MODEL SETTINGS CARD -->
      <section class="panel sql-card">
        <div class="panel-header">
          <div class="panel-title-with-icon">
            <div class="panel-icon-badge" style="background: #e0f2fe; color: #0369a1;">
              <i class="pi pi-sliders-h"></i>
            </div>
            <div>
              <h2>SQL Data Model</h2>
              <p>Configure model name, preview limit, and description</p>
            </div>
          </div>
          <div class="header-actions">
            <p-tag *ngIf="sqlValidation" [value]="sqlValidation.valid ? 'VALID SQL' : 'INVALID SQL'" [severity]="sqlValidation.valid ? 'success' : 'danger'"></p-tag>
            <p-tag *ngIf="selectedSqlQuery" [value]="selectedSqlQuery.published ? 'PUBLISHED' : 'PRIVATE DRAFT'" [severity]="selectedSqlQuery.published ? 'success' : 'warn'"></p-tag>
          </div>
        </div>

        <div class="sql-definition-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input id="sqlQueryName" pInputText [(ngModel)]="sqlQueryName" [disabled]="!canEditSelectedSql()" />
              <label for="sqlQueryName">Data Model Name</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-list"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input id="sqlMaxRows" pInputText type="number" min="1" max="500" [(ngModel)]="sqlMaxRows" />
              <label for="sqlMaxRows">Preview Rows (max 500)</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup class="span-2">
            <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input id="sqlDescription" pInputText [(ngModel)]="sqlDescription" [disabled]="!canEditSelectedSql()" />
              <label for="sqlDescription">Description</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>
      </section>

      <!-- SQL EDITOR PANEL -->
      <section class="panel sql-editor-panel">
        <div class="panel-header">
          <div class="panel-title-with-icon">
            <div class="panel-icon-badge" style="background: #e8f3f6; color: #1f5f74;">
              <i class="pi pi-code"></i>
            </div>
            <div>
              <h2>SELECT Statement</h2>
              <p>Supports joins, CTEs, subqueries, aggregates, windows, and named parameters such as <code>:fromDate</code></p>
            </div>
          </div>
          <div class="chip-row" *ngIf="sqlValidation?.sources?.length">
            <span class="source-detected-pill" *ngFor="let source of sqlValidation?.sources">
              <i class="pi pi-table"></i>
              {{ source }}
            </span>
          </div>
        </div>

        <div #sqlEditorHost class="monaco-host sql-monaco-host" [class.hidden]="sqlEditorFallback"></div>
        <textarea
          *ngIf="sqlEditorFallback"
          pTextarea
          class="template-source-fallback"
          [(ngModel)]="sqlText"
          [disabled]="!canEditSelectedSql()"
          rows="18">
        </textarea>

        <div class="sql-parameter-panel">
          <div class="sql-param-info">
            <div class="param-icon-badge">
              <i class="pi pi-sliders-v"></i>
            </div>
            <div>
              <strong>Parameter Values (JSON)</strong>
              <span>Values bound securely into query. Never concatenated into SQL.</span>
            </div>
          </div>
          <textarea pTextarea [(ngModel)]="sqlParametersText" rows="4" spellcheck="false" class="sql-param-textarea"></textarea>
        </div>

        <pre *ngIf="sqlValidation && !sqlValidation.valid" class="diagnostics danger">{{ sqlDiagnostics() }}</pre>
      </section>

      <!-- SECURE PREVIEW PANEL -->
      <section class="panel preview-panel sql-preview-panel">
        <div class="panel-header">
          <div class="panel-title-with-icon">
            <div class="panel-icon-badge" style="background: #ecfdf5; color: #059669;">
              <i class="pi pi-eye"></i>
            </div>
            <div>
              <h2>Secure Preview</h2>
              <p *ngIf="sqlPreview">
                <span class="preview-stat-pill"><strong>{{ sqlPreview.rowCount }}</strong> rows in <strong>{{ sqlPreview.durationMs }}ms</strong></span>
                <span *ngIf="sqlPreview.truncated" class="preview-truncated-pill">Truncated at max rows</span>
              </p>
              <p *ngIf="!sqlPreview">Validate, then click Run Preview to verify results with bound parameters.</p>
            </div>
          </div>

          <div class="header-actions sql-export-actions" *ngIf="canExportSql">
            <span class="export-caption">Export</span>
            <button
              pButton
              type="button"
              *ngFor="let format of formats"
              [icon]="formatIcon(format)"
              [label]="format.toUpperCase()"
              [outlined]="true"
              size="small"
              [loading]="sqlExportingFormat === format"
              [disabled]="sqlLoading || !!sqlExportingFormat"
              (click)="exportSqlQuery(format)">
            </button>
          </div>
        </div>

        <p-table *ngIf="sqlPreview" [value]="sqlPreview.rows" styleClass="p-datatable-sm p-datatable-gridlines" [paginator]="true" [rows]="10" [scrollable]="true">
          <ng-template pTemplate="header">
            <tr>
              <th *ngFor="let column of sqlPreview.columns">
                <div class="sql-th-content">
                  <span class="sql-th-name">{{ column.name }}</span>
                  <span class="sql-th-type" [ngClass]="sqlColumnTypeClass(column.dataType)">{{ column.dataType }}</span>
                </div>
              </th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td *ngFor="let column of sqlPreview.columns">{{ sqlCell(row, column.name) }}</td>
            </tr>
          </ng-template>
        </p-table>
        <div *ngIf="!sqlPreview" class="empty-state sql-preview-empty">
          <i class="pi pi-table"></i>
          <span>Query results will appear here after clicking <strong>Run Preview</strong>.</span>
        </div>
      </section>
    </main>

    <!-- RIGHT PANE: APPROVED SOURCES -->
    <aside class="catalog-panel sql-source-catalog" [class.collapsed]="rightPaneCollapsed">
      <!-- COLLAPSED RAIL -->
      <div class="right-pane-rail" *ngIf="rightPaneCollapsed">
        <button
          type="button"
          class="rail-toggle-btn"
          (click)="toggleRightPane()"
          title="Expand Approved Sources">
          <i class="pi pi-angle-double-left"></i>
        </button>
        <div class="rail-divider"></div>
        <button
          type="button"
          class="rail-icon-btn"
          (click)="toggleRightPane()"
          title="Approved Sources ({{ sqlSources.length }})">
          <i class="pi pi-table" style="color: #0284c7;"></i>
        </button>
      </div>

      <!-- EXPANDED CONTENT -->
      <div class="right-pane-content" *ngIf="!rightPaneCollapsed">
        <div class="panel sql-sources-card">
          <div class="panel-header source-header">
            <div>
              <h2>Approved Sources</h2>
              <p>Virtual tables & base views</p>
            </div>
            <div class="header-tools">
              <span class="format-count-pill">{{ filteredSqlSources().length }} of {{ sqlSources.length }}</span>
              <button
                type="button"
                class="panel-collapse-btn"
                (click)="toggleRightPane()"
                title="Collapse right pane">
                <i class="pi pi-angle-double-right"></i>
              </button>
            </div>
          </div>

          <div class="tree-search-bar">
            <i class="pi pi-search search-icon"></i>
            <input
              type="text"
              [(ngModel)]="sqlSourceSearch"
              placeholder="Search sources, fields..."
              aria-label="Filter approved sources" />
            <button
              *ngIf="sqlSourceSearch"
              type="button"
              class="clear-search-btn"
              (click)="sqlSourceSearch = ''"
              title="Clear search">
              <i class="pi pi-times"></i>
            </button>
          </div>

          <!-- CATEGORY FILTER CHIPS -->
          <div class="source-cat-pills">
            <button
              type="button"
              class="source-cat-pill"
              [class.active]="sqlSourceCategory === 'ALL'"
              (click)="sqlSourceCategory = 'ALL'">
              All ({{ sqlSources.length }})
            </button>
            <button
              type="button"
              class="source-cat-pill"
              *ngFor="let cat of sqlSourceCategories()"
              [class.active]="sqlSourceCategory === cat"
              (click)="sqlSourceCategory = cat">
              {{ cat }}
            </button>
          </div>

          <!-- SOURCES LIST -->
          <div class="source-items-list">
            <div
              class="source-item-card"
              *ngFor="let source of filteredSqlSources()"
              (click)="selectSqlSource(source)"
              [class.active]="selectedSqlSource?.sourceName === source.sourceName">
              <div class="source-item-top">
                <div class="source-icon-wrap" [style.background]="moduleBg(source.category || '')">
                  <i [class]="moduleIcon(source.category || '')" [style.color]="moduleColor(source.category || '')"></i>
                </div>
                <code class="source-name-code">{{ source.sourceName }}</code>
                <span class="source-cat-tag">{{ source.category || 'General' }}</span>
              </div>
              <div class="source-label-text">{{ source.label }}</div>
              <small class="source-desc-text" *ngIf="source.description">{{ source.description }}</small>
              <div class="source-actions-bar">
                <span class="source-type-pill">{{ source.sourceType || 'BASE_VIEW' }}</span>
                <button
                  type="button"
                  class="source-quick-insert-btn"
                  (click)="insertSqlSource(source); $event.stopPropagation()"
                  title="Insert table name into SQL editor">
                  <i class="pi pi-plus"></i>
                  <span>Insert</span>
                </button>
              </div>
            </div>
            <div *ngIf="!filteredSqlSources().length" class="empty-state">No approved sources match this search.</div>
          </div>

          <!-- SELECTED SOURCE SCHEMA DETAIL DRAWER -->
          <div class="source-columns-detail" *ngIf="selectedSqlSource">
            <div class="source-columns-header">
              <div class="source-cols-title">
                <i class="pi pi-list"></i>
                <strong>{{ selectedSqlSource.sourceName }} Columns</strong>
              </div>
              <button
                pButton
                type="button"
                size="small"
                icon="pi pi-plus"
                label="Insert Source"
                [outlined]="true"
                (click)="insertSqlSource(selectedSqlSource)">
              </button>
            </div>
            <div class="key-columns-banner" *ngIf="selectedSqlSource.keyColumns?.length">
              <i class="pi pi-key"></i>
              <span>Key: {{ selectedSqlSource.keyColumns?.join(', ') }}</span>
            </div>
            <div class="source-column-list">
              <div class="source-col-row" *ngFor="let column of selectedSqlSource.columns">
                <div class="source-col-main">
                  <code class="source-col-name">{{ column.columnName }}</code>
                  <span class="bi-datatype-icon" [ngClass]="sqlColumnTypeClass(column.dataType)">
                    {{ sqlColumnTypeBadge(column.dataType) }}
                  </span>
                </div>
                <small class="source-col-desc" *ngIf="column.description">{{ column.description }}</small>
              </div>
              <div class="source-rel-row" *ngFor="let rel of selectedSqlSource.relationships || []">
                <div class="source-rel-main">
                  <span class="rel-arrow">&rarr;</span>
                  <strong class="rel-target">{{ rel.targetSource }}</strong>
                </div>
                <code class="rel-cond">{{ rel.localColumn }} = {{ rel.targetColumn }}</code>
                <small class="rel-desc" *ngIf="rel.description">{{ rel.description }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  </section>

  <section class="template-workspace" [class.right-collapsed]="rightPaneCollapsed" [class.left-collapsed]="leftPaneCollapsed" *ngIf="activeWorkspace === 'templates'">
    <!-- LEFT PANE: TEMPLATES CATALOG -->
    <aside class="catalog-panel template-models-panel" [class.collapsed]="leftPaneCollapsed">
      <!-- COLLAPSED RAIL -->
      <div class="left-pane-rail" *ngIf="leftPaneCollapsed">
        <button
          type="button"
          class="rail-toggle-btn"
          (click)="toggleLeftPane()"
          title="Expand Templates Catalog">
          <i class="pi pi-angle-double-right"></i>
        </button>
        <div class="rail-divider"></div>
        <button
          type="button"
          class="rail-icon-btn"
          (click)="toggleLeftPane()"
          title="Templates ({{ templates.length }})">
          <i class="pi pi-file-edit" style="color: #0284c7;"></i>
        </button>
      </div>

      <!-- EXPANDED CONTENT -->
      <div class="left-pane-content" *ngIf="!leftPaneCollapsed">
        <div class="panel template-models-card">
          <div class="panel-header tree-header">
            <div>
              <h2>Templates</h2>
              <p>Thymeleaf document definitions</p>
            </div>
            <div class="header-tools">
              <span class="format-count-pill">{{ filteredTemplates().length }} of {{ templates.length }}</span>
              <button
                type="button"
                class="panel-collapse-btn"
                (click)="toggleLeftPane()"
                title="Collapse left pane">
                <i class="pi pi-angle-double-left"></i>
              </button>
            </div>
          </div>

          <div class="tree-search-bar">
            <i class="pi pi-search search-icon"></i>
            <input
              type="text"
              [(ngModel)]="templateSearch"
              placeholder="Search templates, types..."
              aria-label="Filter templates" />
            <button
              *ngIf="templateSearch"
              type="button"
              class="clear-search-btn"
              (click)="templateSearch = ''"
              title="Clear search">
              <i class="pi pi-times"></i>
            </button>
          </div>

          <!-- REPORT TYPE FILTER PILLS -->
          <div class="template-type-pills" *ngIf="templateTypes().length > 1">
            <button
              type="button"
              class="template-type-pill"
              [class.active]="templateTypeFilter === 'ALL'"
              (click)="templateTypeFilter = 'ALL'">
              All ({{ templates.length }})
            </button>
            <button
              type="button"
              class="template-type-pill"
              *ngFor="let tType of templateTypes()"
              [class.active]="templateTypeFilter === tType"
              (click)="templateTypeFilter = tType">
              {{ tType }}
            </button>
          </div>

          <!-- TEMPLATES LIST -->
          <div class="template-items-list" *ngIf="filteredTemplates().length">
            <div
              class="template-item-card"
              *ngFor="let template of filteredTemplates()"
              (click)="openTemplate(template)"
              [class.active]="selectedTemplate?.templateId === template.templateId">
              <div class="template-item-top">
                <div class="template-icon-wrap" [style.background]="moduleBg(template.reportType)">
                  <i class="pi pi-file-edit" [style.color]="moduleColor(template.reportType)"></i>
                </div>
                <div class="template-item-info">
                  <span class="template-item-name">{{ template.templateName }}</span>
                  <div class="template-item-meta">
                    <code class="template-type-code">{{ template.reportType }}</code>
                    <span class="template-version-tag">v{{ template.latestVersion?.versionNumber || 1 }}</span>
                    <span class="template-status-tag" [class.published]="template.status === 'PUBLISHED'">
                      {{ template.status }}
                    </span>
                  </div>
                </div>
                <i *ngIf="selectedTemplate?.templateId === template.templateId" class="pi pi-check active-indicator"></i>
              </div>
            </div>
          </div>

          <div *ngIf="!filteredTemplates().length" class="sql-empty-state">
            <div class="empty-icon-wrap">
              <i class="pi pi-file-edit"></i>
            </div>
            <strong>No Templates Found</strong>
            <p *ngIf="templates.length">No templates match the current filter criteria.</p>
            <p *ngIf="!templates.length">Create standard HTML templates to generate branded PDF and print-ready documents.</p>
            <button
              pButton
              type="button"
              size="small"
              icon="pi pi-plus"
              label="New Template"
              [disabled]="!canCreateTemplates"
              (click)="newTemplate()">
            </button>
          </div>
        </div>
      </div>
    </aside>

    <!-- MIDDLE PANE: TEMPLATE DESIGNER & PREVIEW -->
    <main class="designer-panel template-designer-panel">
      <!-- SECURITY / RENDERER CALLOUT -->
      <div class="security-callout modern-callout">
        <div class="security-callout-icon">
          <i class="pi pi-shield"></i>
        </div>
        <div class="security-callout-content">
          <div class="security-callout-title">
            <strong>Server-Side Thymeleaf Document Rendering</strong>
            <span class="security-badge">HTML &bull; PDF</span>
          </div>
          <p>HTML templates are rendered server-side with strict authorized bindings and audited expression validation. Drafts remain isolated until published.</p>
        </div>
      </div>

      <div *ngIf="templateErrorMessage" class="error-panel">{{ templateErrorMessage }}</div>

      <!-- TEMPLATE DEFINITION SETTINGS CARD -->
      <section class="panel template-card">
        <div class="panel-header">
          <div class="panel-title-with-icon">
            <div class="panel-icon-badge" style="background: #e0f2fe; color: #0369a1;">
              <i class="pi pi-sliders-h"></i>
            </div>
            <div>
              <h2>Template Definition</h2>
              <p>Configure document title, document category type, and revision notes</p>
            </div>
          </div>
          <div class="header-actions">
            <button pButton type="button" icon="pi pi-plus" label="New" [outlined]="true" [disabled]="!canCreateTemplates" (click)="newTemplate()"></button>
            <button pButton type="button" icon="pi pi-save" label="Save Draft" [loading]="templateSaving" [disabled]="selectedTemplate ? !canEditTemplates : !canCreateTemplates" (click)="saveTemplate()"></button>
            <button pButton type="button" icon="pi pi-check-circle" label="Validate" [outlined]="true" [loading]="templateLoading" [disabled]="!canPreviewTemplates && !canEditTemplates" (click)="validateTemplate()"></button>
            <button pButton type="button" icon="pi pi-upload" label="Publish" severity="success" [loading]="templateLoading" [disabled]="!canPublishTemplates" (click)="publishTemplate()"></button>
          </div>
        </div>

        <div class="form-grid template-form-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input id="templateName" pInputText [(ngModel)]="templateName" />
              <label for="templateName">Template Name</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-tags"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select id="selectedReportType" [(ngModel)]="selectedReportType" [options]="reportTypeOptions" appendTo="body" (ngModelChange)="onTemplateReportTypeChange($event)"></p-select>
              <label for="selectedReportType">Report Type</label>
            </p-iftalabel>
          </p-inputgroup>

          <p-inputgroup class="span-2">
            <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input id="templateDescription" pInputText [(ngModel)]="templateDescription" />
              <label for="templateDescription">Description</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>
      </section>

      <!-- THYMELEAF SOURCE & APPROVED BINDINGS GRID -->
      <section class="template-editor-grid">
        <div class="panel source-panel">
          <div class="panel-header">
            <div class="panel-title-with-icon">
              <div class="panel-icon-badge" style="background: #e8f3f6; color: #1f5f74;">
                <i class="pi pi-code"></i>
              </div>
              <div>
                <h2>Thymeleaf Source</h2>
                <p>HTML markup with <code>th:*</code> attributes rendered securely server-side</p>
              </div>
            </div>
            <div class="chip-row" *ngIf="selectedReportType">
              <span class="source-detected-pill">
                <i class="pi pi-tag"></i>
                {{ selectedReportType }}
              </span>
            </div>
          </div>
          <div #templateEditorHost class="monaco-host template-monaco-host" [class.hidden]="templateEditorFallback"></div>
          <textarea
            *ngIf="templateEditorFallback"
            pTextarea
            class="template-source-fallback"
            [(ngModel)]="templateHtml"
            rows="28">
          </textarea>
        </div>

        <aside class="panel binding-panel">
          <div class="panel-header">
            <div class="panel-title-with-icon">
              <div class="panel-icon-badge" style="background: #fef3c7; color: #d97706;">
                <i class="pi pi-tags"></i>
              </div>
              <div>
                <h2>Approved Bindings</h2>
                <p>{{ filteredBindingFields().length }} of {{ bindingFields().length }} fields</p>
              </div>
            </div>
          </div>

          <div class="tree-search-bar" *ngIf="bindingFields().length">
            <i class="pi pi-search search-icon"></i>
            <input
              type="text"
              [(ngModel)]="bindingSearch"
              placeholder="Search bindings..."
              aria-label="Filter bindings" />
            <button
              *ngIf="bindingSearch"
              type="button"
              class="clear-search-btn"
              (click)="bindingSearch = ''"
              title="Clear search">
              <i class="pi pi-times"></i>
            </button>
          </div>

          <div class="binding-list" *ngIf="filteredBindingFields().length">
            <div class="binding-item-card" *ngFor="let field of filteredBindingFields()" (click)="insertBinding(field)">
              <div class="binding-item-top">
                <strong class="binding-field-label">{{ field.fieldLabel || field.fieldPath || field.rootName }}</strong>
                <span class="binding-java-type">{{ field.javaType }}</span>
              </div>
              <div class="binding-expr-row">
                <code class="binding-expr-snippet">${{ '{' + (field.fieldPath || field.rootName) + '}' }}</code>
                <button
                  type="button"
                  class="source-quick-insert-btn"
                  (click)="insertBinding(field); $event.stopPropagation()"
                  title="Insert expression into Thymeleaf editor">
                  <i class="pi pi-plus"></i>
                  <span>Insert</span>
                </button>
              </div>
              <small class="binding-desc-text" *ngIf="field.sampleJson">Sample: {{ field.sampleJson }}</small>
            </div>
          </div>

          <div *ngIf="!filteredBindingFields().length" class="sql-empty-state" style="padding: 1rem 0.5rem;">
            <small style="color: #64748b;">No approved bindings match search.</small>
          </div>
        </aside>
      </section>

      <!-- SAMPLE DATA & LIVE PREVIEW PANEL -->
      <section class="panel template-preview-card">
        <div class="panel-header">
          <div class="panel-title-with-icon">
            <div class="panel-icon-badge" style="background: #ecfdf5; color: #059669;">
              <i class="pi pi-eye"></i>
            </div>
            <div>
              <h2>Sample Data And Preview</h2>
              <p>Test layout, edge cases, and conditional blocks with mock JSON payload before publishing</p>
            </div>
          </div>
          <div class="header-actions">
            <button pButton type="button" icon="pi pi-eye" label="Preview HTML" [outlined]="true" [loading]="reportActions.isPending('template-preview:HTML')" [disabled]="!canPreviewTemplates" (click)="previewTemplate('HTML')"></button>
            <button pButton type="button" icon="pi pi-file-pdf" label="Preview PDF" [loading]="reportActions.isPending('template-preview:PDF')" [disabled]="!canPreviewTemplates" (click)="previewTemplate('PDF')"></button>
          </div>
        </div>

        <div class="preview-grid">
          <div class="sample-json-container">
            <div class="sample-json-header">
              <span class="sample-json-title"><i class="pi pi-code"></i> Context JSON Payload</span>
            </div>
            <textarea pTextarea class="sample-json" [(ngModel)]="sampleContextText" rows="18" spellcheck="false"></textarea>
          </div>
          <div class="template-preview-container">
            <div class="template-preview-header">
              <span class="template-preview-title"><i class="pi pi-desktop"></i> Live Output</span>
              <span *ngIf="templatePreview?.html" class="preview-stat-pill"><i class="pi pi-check"></i> Rendered</span>
            </div>
            <div class="template-preview-box">
              <iframe *ngIf="templatePreview?.html" [srcdoc]="templatePreview?.html"></iframe>
              <div *ngIf="!templatePreview?.html" class="sql-preview-empty" style="height: 100%; min-height: 20rem;">
                <i class="pi pi-eye"></i>
                <span>Click Preview HTML or Preview PDF to render this template.</span>
              </div>
            </div>
          </div>
        </div>

        <pre *ngIf="templatePreview?.diagnostics?.length" class="diagnostics">{{ diagnostics() }}</pre>
      </section>
    </main>

    <!-- RIGHT PANE: VERSION HISTORY -->
    <aside class="catalog-panel template-versions-panel" [class.collapsed]="rightPaneCollapsed">
      <!-- COLLAPSED RAIL -->
      <div class="right-pane-rail" *ngIf="rightPaneCollapsed">
        <button
          type="button"
          class="rail-toggle-btn"
          (click)="toggleRightPane()"
          title="Expand Version History">
          <i class="pi pi-angle-double-left"></i>
        </button>
        <div class="rail-divider"></div>
        <button
          type="button"
          class="rail-icon-btn"
          (click)="toggleRightPane()"
          title="Version History ({{ templateVersions.length }})">
          <i class="pi pi-history" style="color: #9333ea;"></i>
        </button>
      </div>

      <!-- EXPANDED CONTENT -->
      <div class="right-pane-content" *ngIf="!rightPaneCollapsed">
        <div class="panel template-versions-card">
          <div class="panel-header inspector-header">
            <div>
              <h2>Versions</h2>
              <p>Revision history</p>
            </div>
            <div class="header-tools">
              <span class="format-count-pill">{{ templateVersions.length }} Versions</span>
              <button
                type="button"
                class="panel-collapse-btn"
                (click)="toggleRightPane()"
                title="Collapse right pane">
                <i class="pi pi-angle-double-right"></i>
              </button>
            </div>
          </div>

          <div class="template-versions-list" *ngIf="templateVersions.length">
            <div
              class="version-item-card"
              *ngFor="let version of templateVersions">
              <div class="version-item-top">
                <span class="version-item-badge">Version {{ version.versionNumber }}</span>
                <span class="version-status-tag" [class.published]="version.status === 'PUBLISHED'">
                  {{ version.status }}
                </span>
              </div>
              <div class="version-item-time">
                <i class="pi pi-clock"></i>
                <span>{{ version.updatedAt | date:'medium' }}</span>
              </div>
              <div class="version-item-actions">
                <button
                  pButton
                  type="button"
                  size="small"
                  icon="pi pi-replay"
                  label="Restore"
                  [outlined]="true"
                  [disabled]="!canEditTemplates"
                  (click)="restoreVersion(version)">
                </button>
              </div>
            </div>
          </div>

          <div *ngIf="!templateVersions.length" class="sql-empty-state">
            <div class="empty-icon-wrap" style="background: #f3e8ff; color: #9333ea;">
              <i class="pi pi-history"></i>
            </div>
            <strong>No Versions Yet</strong>
            <p>Publishing or saving creates immutable revision snapshots that you can restore anytime.</p>
          </div>
        </div>
      </div>
    </aside>
  </section>
</div>
